import {
  HTMLAttributes,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import './SplitPane.css';

export interface SplitPaneProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
> {
  /** Content for the top section */
  top: ReactNode;
  /** Content for the bottom section */
  bottom: ReactNode;
  /** Whether the bottom panel is open */
  open: boolean;
  /** Minimum height of the bottom panel as percentage of container (0-1) */
  minBottomRatio?: number;
  /** Maximum height of the bottom panel as percentage of container (0-1) */
  maxBottomRatio?: number;
  /** Default height of the bottom panel as percentage of container (0-1) */
  defaultBottomRatio?: number;
  /** localStorage key for persisting the split ratio */
  storageKey?: string;
  /** Called when the panel is collapsed via drag or keyboard */
  onCollapse?: () => void;
  /** Hint text shown when collapsed */
  collapsedHint?: string;
}

export const SplitPane = forwardRef<HTMLDivElement, SplitPaneProps>(
  (
    {
      top,
      bottom,
      open,
      minBottomRatio = 0.3,
      maxBottomRatio = 0.7,
      defaultBottomRatio = 0.55,
      storageKey = 'splitpane-ratio',
      onCollapse,
      collapsedHint,
      className = '',
      ...props
    },
    ref
  ) => {
    const [bottomRatio, setBottomRatio] = useState<number>(() => {
      if (storageKey) {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = parseFloat(stored);
          if (!isNaN(parsed) && parsed >= 0.1 && parsed <= 0.9) return parsed;
        }
      }
      return defaultBottomRatio;
    });

    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const startYRef = useRef(0);
    const startRatioRef = useRef(0);

    // Persist ratio to localStorage
    useEffect(() => {
      if (storageKey && !isDragging) {
        localStorage.setItem(storageKey, String(bottomRatio));
      }
    }, [bottomRatio, isDragging, storageKey]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        startYRef.current = e.clientY;
        startRatioRef.current = bottomRatio;
      },
      [bottomRatio]
    );

    useEffect(() => {
      if (!isDragging) return;

      const handleMouseMove = (e: MouseEvent) => {
        const container = containerRef.current?.parentElement;
        if (!container) return;
        const containerHeight = container.getBoundingClientRect().height;
        if (containerHeight === 0) return;

        const deltaPixels = startYRef.current - e.clientY;
        const deltaRatio = deltaPixels / containerHeight;
        const newRatio = Math.min(
          maxBottomRatio,
          Math.max(minBottomRatio, startRatioRef.current + deltaRatio)
        );
        setBottomRatio(newRatio);
      };

      const handleMouseUp = (e: MouseEvent) => {
        setIsDragging(false);
        const container = containerRef.current?.parentElement;
        if (!container) return;
        const containerHeight = container.getBoundingClientRect().height;
        if (containerHeight === 0) return;

        const deltaPixels = startYRef.current - e.clientY;
        const deltaRatio = deltaPixels / containerHeight;
        const finalRatio = startRatioRef.current + deltaRatio;
        if (finalRatio < minBottomRatio * 0.5) {
          onCollapse?.();
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'row-resize';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }, [isDragging, minBottomRatio, maxBottomRatio, onCollapse]);

    const classNames = [
      'ui-split-pane',
      !open && 'ui-split-pane--collapsed',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const topPercent = Math.round((1 - bottomRatio) * 100);
    const bottomPercent = Math.round(bottomRatio * 100);

    return (
      <div ref={ref} className={classNames} {...props}>
        <div
          className="ui-split-pane__top"
          ref={containerRef}
          style={open ? { flex: `0 0 ${topPercent}%` } : undefined}
        >
          {top}
        </div>
        {open ? (
          <>
            <div
              className={`ui-split-pane__handle${isDragging ? ' ui-split-pane__handle--dragging' : ''}`}
              onMouseDown={handleMouseDown}
              role="separator"
              aria-orientation="horizontal"
              aria-valuenow={bottomPercent}
              aria-valuemin={Math.round(minBottomRatio * 100)}
              aria-valuemax={Math.round(maxBottomRatio * 100)}
              tabIndex={0}
            >
              <div className="ui-split-pane__handle-grip" />
            </div>
            <div
              className="ui-split-pane__bottom"
              style={{ flex: `0 0 ${bottomPercent}%` }}
            >
              {bottom}
            </div>
          </>
        ) : (
          collapsedHint && (
            <div className="ui-split-pane__hint">{collapsedHint}</div>
          )
        )}
      </div>
    );
  }
);

SplitPane.displayName = 'SplitPane';
